import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useNotifications } from '@byscott-io/corebyscott';
import { recipes, recipeCategories } from '../api/client';
import RecipeForm from '../components/recipes/RecipeForm';
import Modal from '../components/common/Modal';
import RecipeCardScanner from '../components/recipes/RecipeCardScanner';
import RecipeBrowser from '../components/recipes/RecipeBrowser';

export default function RecipesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [showNewModal, setShowNewModal] = useState(searchParams.get('new') === 'true');
  const [showImportModal, setShowImportModal] = useState(false);
  const [showScanModal, setShowScanModal] = useState(false);
  const [showImportMenu, setShowImportMenu] = useState(false);
  const [scannedRecipeData, setScannedRecipeData] = useState(null);
  const [importUrl, setImportUrl] = useState('');
  const [importing, setImporting] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const notifications = useNotifications();

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    if (searchParams.get('new') === 'true') {
      setShowNewModal(true);
      searchParams.delete('new');
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams]);

  const loadCategories = async () => {
    try {
      const response = await recipeCategories.list();
      setCategories(response.data.categories);
    } catch {
      notifications.error('Failed to load categories');
    }
  };

  const handleCreateRecipe = async (data) => {
    try {
      await recipes.create({ recipe: data, category_ids: data.category_ids });
      notifications.success('Recipe created!');
      setShowNewModal(false);
      setScannedRecipeData(null);
      setRefreshKey((k) => k + 1); // Trigger refresh
    } catch (err) {
      notifications.error(err.response?.data?.errors?.join(', ') || 'Failed to create recipe');
    }
  };

  const handleRecipeScanned = (data) => {
    setScannedRecipeData(data);
    setShowScanModal(false);
    setShowNewModal(true);
  };

  const handleImport = async () => {
    if (!importUrl.trim()) return;
    try {
      setImporting(true);
      const response = await recipes.importFromUrl(importUrl);
      if (response.data.success) {
        notifications.success(`Imported "${response.data.recipe.title}"!`);
        setShowImportModal(false);
        setImportUrl('');
        setRefreshKey((k) => k + 1); // Trigger refresh
      } else {
        notifications.error(response.data.error || 'Import failed');
      }
    } catch (err) {
      notifications.error(err.response?.data?.error || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleImportClick = () => {
    setShowImportMenu(!showImportMenu);
  };

  return (
    <div className="px-4 py-4">
      {/* Import Menu Dropdown (positioned absolutely when open) */}
      {showImportMenu && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setShowImportMenu(false)} />
          <div className="absolute right-4 top-16 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-20">
            <button
              onClick={() => { setShowScanModal(true); setShowImportMenu(false); }}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-t-lg flex items-center gap-2"
            >
              <span>📷</span> Scan Recipe Card
            </button>
            <button
              onClick={() => { setShowImportModal(true); setShowImportMenu(false); }}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-b-lg flex items-center gap-2"
            >
              <span>🔗</span> Import from URL
            </button>
          </div>
        </>
      )}

      {/* Recipe Browser */}
      <RecipeBrowser
        key={refreshKey}
        showActions={true}
        defaultMyKitchen={true}
        detailPath="/app/recipes"
        onAddRecipe={() => setShowNewModal(true)}
        onImportClick={handleImportClick}
      />

      {/* New Recipe Modal */}
      <Modal
        isOpen={showNewModal}
        onClose={() => {
          setShowNewModal(false);
          setScannedRecipeData(null);
        }}
        title={scannedRecipeData ? 'Review Scanned Recipe' : 'Add New Recipe'}
        size="lg"
      >
        <RecipeForm
          categories={categories}
          initialData={scannedRecipeData}
          onSubmit={handleCreateRecipe}
          onCancel={() => {
            setShowNewModal(false);
            setScannedRecipeData(null);
          }}
        />
      </Modal>

      {/* Recipe Card Scanner Modal */}
      <RecipeCardScanner
        isOpen={showScanModal}
        onClose={() => setShowScanModal(false)}
        onRecipeExtracted={handleRecipeScanned}
      />

      {/* Import Recipe Modal */}
      <Modal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Import Recipe from URL"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Paste a URL from a recipe website. We'll extract the recipe details automatically.
          </p>
          <input
            type="url"
            placeholder="https://example.com/recipe/chocolate-chip-cookies"
            value={importUrl}
            onChange={(e) => setImportUrl(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleImport()}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
            disabled={importing}
          />
          <p className="text-xs text-gray-500 dark:text-gray-500">
            Works with most recipe sites that use structured data (AllRecipes, Food Network, Serious Eats, etc.)
          </p>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setShowImportModal(false)}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              disabled={importing}
            >
              Cancel
            </button>
            <button
              onClick={handleImport}
              disabled={importing || !importUrl.trim()}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {importing ? 'Importing...' : 'Import Recipe'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
